import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Select } from "antd";
import ti18n from 'i18n!nls/Sequenceri18n';

const EditorSearchParamsForSilenceSelect = ({
    searchParams,
    onSearchParamsChange,
    searchWidgetModel
}) => {
    const SILENCE_THRESHOLD = {
        FAST: 0,
        SMOOTH: 1,
        RELAXED: 2
    };

    const THRESHOLD_CONFIG = [
        {
            key: SILENCE_THRESHOLD.FAST,
            seconds: 0,
            labelKey: 'SILENCE_THRESHOLD_FAST_LABEL',
            ruleLabelKey: 'SILENCE_THRESHOLD_FAST_RULE_LABEL',
            helperKey: 'SILENCE_THRESHOLD_FAST_HELPER',
            platformsKey: 'SILENCE_THRESHOLD_FAST_PLATFORMS'
        },
        {
            key: SILENCE_THRESHOLD.SMOOTH,
            seconds: 1,
            labelKey: 'SILENCE_THRESHOLD_SMOOTH_LABEL',
            ruleLabelKey: 'SILENCE_THRESHOLD_SMOOTH_RULE_LABEL',
            helperKey: 'SILENCE_THRESHOLD_SMOOTH_HELPER',
            platformsKey: 'SILENCE_THRESHOLD_SMOOTH_PLATFORMS'
        },
        {
            key: SILENCE_THRESHOLD.RELAXED,
            seconds: 3,
            labelKey: 'SILENCE_THRESHOLD_RELAXED_LABEL',
            ruleLabelKey: 'SILENCE_THRESHOLD_RELAXED_RULE_LABEL',
            helperKey: 'SILENCE_THRESHOLD_RELAXED_HELPER',
            platformsKey: 'SILENCE_THRESHOLD_RELAXED_PLATFORMS'
        }
    ];

    const getInitialValue = () => {
        const ms = searchParams?.maxSilenceInMilliseconds ?? 1000;
        const sec = typeof ms === 'number' && !Number.isNaN(ms) ? ms / 1000 : 1;
        if (sec <= 0) return SILENCE_THRESHOLD.FAST;
        if (sec <= 1) return SILENCE_THRESHOLD.SMOOTH;
        return SILENCE_THRESHOLD.RELAXED;
    };

    const [value, setValue] = useState(getInitialValue());
    const [isApplying, setIsApplying] = useState(false);

    useEffect(() => {
        setValue(getInitialValue());
    }, [searchParams]);

    const handleChange = async (newValue) => {
        setValue(newValue);
        const config = THRESHOLD_CONFIG.find(cfg => cfg.key === newValue);
        if (!config) {
            console.error('Invalid silence threshold selection:', newValue);
            return;
        }

        // Fallback: update params even if model is missing or method not available
        if (!searchWidgetModel || typeof searchWidgetModel.applySearchResultsForSilencesToTrim !== 'function') {
            if (onSearchParamsChange) {
                onSearchParamsChange({ maxSilenceInMilliseconds: config.seconds * 1000 });
            }
            return;
        }

        setIsApplying(true);
        try {
            await searchWidgetModel.applySearchResultsForSilencesToTrim(
                config.seconds * 1000,
                true,
                false,
                false
            );
            if (onSearchParamsChange) {
                onSearchParamsChange({
                    maxSilenceInMilliseconds: config.seconds * 1000
                });
            }
        } catch (error) {
            console.error('Error applying silence search:', error);
        } finally {
            setIsApplying(false);
        }
    };

    const options = THRESHOLD_CONFIG.map((config) => {
        return {
            value: config.key,
            label: (
                <div style={{ padding: 4 }}>
                    <div className="font-bold" style={{ whiteSpace: 'nowrap' }}>{window.getI18n(ti18n, config.ruleLabelKey)}</div>
                    <div className="mt-1 text-xs">{window.getI18n(ti18n, config.helperKey)} For: {window.getI18n(ti18n, config.platformsKey)}</div>
                </div>
            )
        };
    });

    return (
        <div className="flex flex-col px-2">
            <Select
                value={value}
                onChange={handleChange}
                disabled={isApplying}
                style={{ width: 260 }}
                options={options}
                dropdownStyle={{ minWidth: 300 }}
                optionLabelProp="label"
            />
        </div>
    );
};

EditorSearchParamsForSilenceSelect.propTypes = {
    searchParams: PropTypes.shape({
        maxSilenceInMilliseconds: PropTypes.number
    }),
    onSearchParamsChange: PropTypes.func,
    searchWidgetModel: PropTypes.shape({
        applySearchResultsForSilencesToTrim: PropTypes.func.isRequired
    }).isRequired
};

export default EditorSearchParamsForSilenceSelect; 